import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IOption } from './index.context'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'
import { getUserProfile } from 'src/app/store/auth'
import { getAccountAssigned, getClientAssigned } from 'src/app/shared/services/ClientSetupService'
import { USER_ROLE } from 'src/app/shared/constants/app.constants'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { getReasonCodes } from 'src/app/shared/services/MasterSetupService'
import { IGetReasonCodes } from 'src/app/shared/models/systemConfiguration'
import { QuickFilter } from '../search-result/index.context'
import { convertAllSecCode } from 'src/app/shared/utils'

export interface IClient {
  id: string
  clientName: string
}

export interface IAccount {
  accountName: string
  id: string
}

export interface UseGetOptionReturn {
  allClientValue: IOption
  allAccountValue: IOption
  transactionOptions: Array<IOption>
  clientOptions: Array<IOption>
  accountOptions: Array<IOption>
  dateTypeOptions: Array<IOption>
  decisionOptions: Array<IOption>
  secCodeOptions: Array<IOption>
  reasonOptions: Array<IOption>
  checkDateTypeOptions: Array<IOption>
  checkStatusOptions: Array<IOption>
  reasonCodeOptions: Array<IOption>
  getReasonCodesOptions: (payload: IGetReasonCodes.Payload) => Promise<any>
}

export interface UseClientReturn {
  loadingClients: boolean
  loadingAccounts: boolean
  clients: Array<IClient>
  accounts: Array<IAccount>
  handleGetClients: () => Promise<Array<IClient>>
  handleGetAccounts: (clientIds: string[]) => Promise<Array<IAccount>>
}

export const useGetOptions = (params: { clients: IClient[]; accounts: IAccount[] }): UseGetOptionReturn => {
  const { t } = useTranslation()
  const { clients, accounts } = params
  const lookupValues = useSelector(getLookupValues)
  const [reasonOptions, setReasonOptions] = useState<IOption[]>([])
  const allClientValue: IOption = useMemo(
    () => ({
      label: t('AllTransactions:title.all_clients'),
      value: null,
    }),
    [t],
  )
  const allAccountValue: IOption = useMemo(
    () => ({
      label: t('AllTransactions:title.all_accounts'),
      value: null,
    }),
    [t],
  )
  const allCheckStatusValue: IOption = useMemo(
    () => ({
      label: t('AllTransactions:title.all'),
      value: null,
    }),
    [t],
  )
  const allSecCodeValue: IOption = useMemo(
    () => ({
      label: ' ALL SEC CODES',
      value: null,
    }),
    [],
  )

  const clientOptions = useMemo(
    () => [
      allClientValue,
      ...clients.map((item) => ({
        label: item.clientName,
        value: item.id,
      })),
    ],
    [allClientValue, clients],
  )

  const accountOptions = useMemo(
    () => [
      allAccountValue,
      ...accounts.map((item) => ({
        label: item.accountName,
        value: item.id,
      })),
    ],
    [accounts, allAccountValue],
  )

  const transactionOptions: Array<IOption> = useMemo(
    () => [
      {
        label: t('AllTransactions:title.all_transactions'),
        value: QuickFilter.ALL,
      },
      {
        label: t('AllTransactions:title.ach'),
        value: QuickFilter.ACH,
      },
      {
        label: t('AllTransactions:title.check'),
        value: QuickFilter.CHECKS,
      },
    ],
    [t],
  )

  const dateTypeOptions: Array<IOption> = useMemo(
    () => [
      {
        label: t('AllTransactions:title.paid'),
        value: 1,
      },
      {
        label: t('AllTransactions:title.input'),
        value: 2,
      },
    ],
    [t],
  )

  const checkDateTypeOptions: Array<IOption> = useMemo(
    () => [
      {
        label: t('AllTransactions:title.issued'),
        value: 0,
      },
      {
        label: t('AllTransactions:title.paid'),
        value: 1,
      },
      {
        label: t('AllTransactions:title.input'),
        value: 2,
      },
      {
        label: t('AllTransactions:title.exception'),
        value: 3,
      },
      {
        label: t('AllTransactions:title.void'),
        value: 4,
      },
      {
        label: t('AllTransactions:title.stale'),
        value: 5,
      },
    ],
    [t],
  )

  const decisionOptions: Array<IOption> = useMemo(
    () =>
      lookupValues?.DefaultCheckDecision.map((item) => ({
        label: item.label,
        value: item.value,
      })) ?? [],
    [lookupValues?.DefaultCheckDecision],
  )

  const secCodeOptions: Array<IOption> = useMemo(
    () => [
      allSecCodeValue,
      ...(convertAllSecCode(lookupValues?.SECCode, true)?.map((item: any) => ({
        label: item.value,
        value: item.value,
      })) ?? []),
    ],
    [allSecCodeValue, lookupValues?.SECCode],
  )

  const checkStatusOptions: Array<IOption> = useMemo(
    () => [
      allCheckStatusValue,
      ...(lookupValues?.CheckStatus.filter((item) => item.value !== 'OS').map((item) => ({
        label:
          item.value !== 'ISSUED'
            ? item.label
            : `${t('AllTransactions:title.issued')}/${t('AllTransactions:title.outstanding')}`,
        value: item.value,
      })) ?? []),
    ],
    [allCheckStatusValue, lookupValues?.CheckStatus, t],
  )

  const getReasonCodesOptions = useCallback(async (payload: IGetReasonCodes.Payload) => {
    try {
      const res = await getReasonCodes(payload)
      if (res.statusCode === STATUS_CODES.SUCCESS) {
        setReasonOptions(res.data.map((item) => ({ label: item.label, value: item.value })))
      }
    } catch (error) {
      console.log('ERROR: ', error)
    }
  }, [])

  const reasonCodeOptions: Array<IOption> = useMemo(
    () => lookupValues?.ReasonCode.map((item) => ({ label: item.label, value: item.value })) ?? [],
    [lookupValues?.ReasonCode],
  )

  return {
    allClientValue,
    allAccountValue,
    transactionOptions,
    clientOptions,
    accountOptions,
    dateTypeOptions,
    decisionOptions,
    secCodeOptions,
    reasonOptions,
    checkDateTypeOptions,
    checkStatusOptions,
    reasonCodeOptions,
    getReasonCodesOptions,
  }
}

export const useGetClientAssigned = (): UseClientReturn => {
  const [loading, setLoading] = useState<boolean>(false)
  const [clients, setClients] = useState<UseClientReturn['clients']>([])
  const [accounts, setAccounts] = useState<UseClientReturn['accounts']>([])
  const { userId, role } = useSelector(getUserProfile)
  const isBankUser = useMemo(() => role === USER_ROLE.BANK, [role])

  const handleGetClients = useCallback(async () => {
    try {
      setLoading(true)
      const res = await getClientAssigned({
        isBankUser,
        userId,
      })
      if (res.statusCode === STATUS_CODES.SUCCESS) {
        setClients(res.data)
        return res.data
      }
      return []
    } catch (error) {
      console.log('ERROR: ', error)
      return []
    } finally {
      setLoading(false)
    }
  }, [isBankUser, userId])

  const handleGetAccounts = useCallback(
    async (clientIds: string[]) => {
      try {
        setLoading(true)
        const res = await getAccountAssigned({
          clientIds,
          isBankUser,
          userId,
        })
        if (res.statusCode === STATUS_CODES.SUCCESS) {
          setAccounts(res.data)
          return res.data
        }
        return []
      } catch (error) {
        console.log('ERROR: ', error)
        return []
      } finally {
        setLoading(false)
      }
    },
    [isBankUser, userId],
  )

  return {
    loadingClients: loading,
    loadingAccounts: loading,
    clients,
    accounts,
    handleGetClients,
    handleGetAccounts,
  }
}
